import { useEffect, useRef, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { calculateImpressions } from '#src/app/shared/utils/promote';

import { usePromoteFormData, usePromoteFormState } from '../../../../formContext';

type Result = ModelHookResult<{
	text: string;
	display: boolean;
}>;

const DEBOUNCE_DELAY = 300;

export function useImpressionCalculation(): Result {
	const { amountToBaseUnits, localizeNumber } = useI18n();

	const {
		currency,
		promotionSettings: { bonusCpm, bonusThreshold, cpm },
	} = usePromoteFormData();
	const [{ amount }] = usePromoteFormState();

	const amountForImpressionCalculation = amount;

	const impressionCount = calculateImpressions(
		amountToBaseUnits(amountForImpressionCalculation, currency.code),
		cpm,
		bonusCpm,
		bonusThreshold,
	);

	const [debouncedImpressionCount, setDebouncedImpressionCount] = useState(impressionCount);
	const prevAmountRef = useRef(amount);

	useEffect(() => {
		const fromZero = prevAmountRef.current === 0;
		prevAmountRef.current = amount;

		if (impressionCount <= 0) return undefined;

		const delay = fromZero ? 0 : DEBOUNCE_DELAY;
		const timeout = setTimeout(() => {
			setDebouncedImpressionCount(impressionCount);
		}, delay);
		return () => clearTimeout(timeout);
	}, [impressionCount, amount]);

	const localizedImpressionCount = localizeNumber(debouncedImpressionCount);

	return {
		data: {
			display: amount > 0,
			text: `Drive <strong>${localizedImpressionCount}</strong> more petition views`,
		},
	};
}
